// Mantenimiento M1: lógica pura del inventario (sin Supabase). La base aplica las mismas reglas (M50): aquí se avisan
// antes de guardar y se prueban.
//  - Código automático PREFIJO-0001 por empresa y categoría (lo cambia solo el administrador).
//  - Estado operativo y aptitud de seguridad son independientes. Entrar a mantenimiento o fuera de servicio deja la
//    aptitud en "no apto"; salir no la vuelve "apta".
//  - Liberar (fuera de servicio → operativo o con observaciones, o no apto → apto) exige un motivo y nunca es masivo.
//  - Vencimientos de los extintores: plazos de la NTE INEN 739:2016 y del AM 1257, que la empresa puede acortar (no
//    alargar). La base no deja registrar una recarga con la prueba hidrostática vencida ni en un extintor no
//    recargable; aquí se avisa antes. Los demás equipos de emergencia: criterios sugeridos por StorSafe, no normativos.

import { fechaEc, isoLocal, sumarMeses } from '../inspecciones/insp-logica';
import {
  CATEGORIAS, ETIQUETA_APTITUD, ETIQUETA_ESTADO, etiquetaCategoria, PLAZOS_EXTINTOR, PLAZOS_NORMA, textoPlazo,
  type Aptitud, type Categoria, type ClavePlazoExtintor, type EstadoOperativo, type FilaActivo, type PlazosExtintor,
} from './mant-tipos';

// ── Código ──
export const prefijoDe = (c: string) => CATEGORIAS[c as Categoria]?.prefijo ?? 'ACT';

/** Siguiente código libre de la categoría en la empresa (el mismo cálculo que la base). */
export function siguienteCodigo(categoria: string, codigosEmpresa: string[]): string {
  const p = prefijoDe(categoria);
  const re = new RegExp(`^${p}-(\\d{1,9})$`);
  const max = codigosEmpresa.reduce((m, c) => { const x = re.exec((c ?? '').trim().toUpperCase()); return x ? Math.max(m, Number(x[1])) : m; }, 0);
  return `${p}-${String(max + 1).padStart(4, '0')}`;
}

export const normalizarCodigo = (c: string) => c.trim().toUpperCase();
export const codigoRepetido = (codigo: string, codigosEmpresa: string[], propio?: string) =>
  codigosEmpresa.some(c => normalizarCodigo(c) === normalizarCodigo(codigo) && normalizarCodigo(c) !== normalizarCodigo(propio ?? '\u0000'));

// ── Estado y aptitud ──
export interface EstadoAptitud { estado: EstadoOperativo; aptitud: Aptitud }

/** Lo que queda guardado tras pedir un cambio (la base fuerza "no apto" al entrar a mantenimiento o fuera de servicio). */
export function resultadoCambio(antes: EstadoAptitud, pedido: Partial<EstadoAptitud>): EstadoAptitud {
  const estado = pedido.estado ?? antes.estado;
  let aptitud = pedido.aptitud ?? antes.aptitud;
  if (estado !== antes.estado && (estado === 'en_mantenimiento' || estado === 'fuera_servicio')) aptitud = 'no_apto';
  return { estado, aptitud };
}

/** ¿El cambio libera el equipo? (exige motivo y no se hace en masa) */
export function esLiberacion(antes: EstadoAptitud, despues: EstadoAptitud): boolean {
  return (antes.estado === 'fuera_servicio' && (despues.estado === 'operativo' || despues.estado === 'con_observaciones'))
    || (antes.aptitud === 'no_apto' && despues.aptitud === 'apto');
}

export const MIN_MOTIVO = 5;

/** Error para mostrar (o null si el cambio se puede guardar). cantidad = activos que cambiarían a la vez. */
export function validarCambio(antes: EstadoAptitud, pedido: Partial<EstadoAptitud>, motivo: string, cantidad = 1): string | null {
  const despues = resultadoCambio(antes, pedido);
  const cambiaAptitud = despues.aptitud !== antes.aptitud;
  const libera = esLiberacion(antes, despues);
  if (cantidad > 1 && (cambiaAptitud || libera)) {
    return 'La aptitud de seguridad y la liberación de equipos se cambian de a un activo, con su motivo (no en masa).';
  }
  if (libera && motivo.trim().length === 0) return 'Para liberar el equipo (pasar a operativo o a apto) escribe el motivo; queda en el historial.';
  if (libera && motivo.trim().length < MIN_MOTIVO) return 'El motivo es muy corto: explica qué se verificó.';
  return null;
}

/** Aviso que se muestra antes de guardar (sin bloquear). */
export function avisoCambio(antes: EstadoAptitud, pedido: Partial<EstadoAptitud>): string | null {
  const d = resultadoCambio(antes, pedido);
  if (d.estado !== antes.estado && (d.estado === 'en_mantenimiento' || d.estado === 'fuera_servicio') && antes.aptitud === 'apto') {
    return `Al pasar a "${ETIQUETA_ESTADO[d.estado]}" la aptitud queda en "${ETIQUETA_APTITUD.no_apto}": al terminar, verifica el equipo y márcalo apto con el motivo.`;
  }
  if (antes.estado === 'fuera_servicio' && d.estado !== 'fuera_servicio' && d.aptitud === 'no_apto') {
    return `El equipo sigue "${ETIQUETA_APTITUD.no_apto}" hasta que alguien verifique y lo marque apto con el motivo.`;
  }
  return null;
}

// ── Acciones masivas autorizadas ──
export type AccionMasiva = 'responsable' | 'ubicacion' | 'categoria' | 'archivar' | 'exportar';
export const ACCIONES_MASIVAS: AccionMasiva[] = ['responsable', 'ubicacion', 'categoria', 'archivar', 'exportar'];
export const ETIQUETA_ACCION: Record<AccionMasiva, string> = {
  responsable: 'Asignar responsable', ubicacion: 'Cambiar ubicación', categoria: 'Cambiar categoría', archivar: 'Archivar', exportar: 'Exportar a Excel',
};
/** Columnas que una acción masiva puede tocar: nunca el estado operativo ni la aptitud. */
export const CAMPOS_MASIVOS: Record<Exclude<AccionMasiva, 'exportar'>, string[]> = {
  responsable: ['responsible_worker_id', 'responsible_user_id'],
  ubicacion: ['site_id', 'area', 'location_detail'],
  categoria: ['category'],
  archivar: ['archived_at'],
};
export function cambioMasivoPermitido(cambio: Record<string, unknown>): boolean {
  const permitidos = new Set(Object.values(CAMPOS_MASIVOS).flat());
  return Object.keys(cambio).length > 0 && Object.keys(cambio).every(k => permitidos.has(k));
}

// ── Vencimientos ──
export type EstadoVencimiento = 'vencido' | 'por_vencer' | 'vigente';
export interface Vencimiento { clave: string; etiqueta: string; fecha: string; estado: EstadoVencimiento; calculado: boolean }
export const DIAS_POR_VENCER = 30;
/** La inspección visual (cada 31 días como máximo) avisa una semana antes: con 30 días estaría siempre "por vencer". */
export const DIAS_AVISO_INSPECCION_VISUAL = 7;
export const NOTA_VENCIMIENTOS = 'Extintores: plazos de la NTE INEN 739:2016 y del AM 1257 (la empresa los puede acortar en "Plazos de extintores"). '
  + 'Los demás plazos son sugeridos por StorSafe, no normativos: lámpara de emergencia mensual; botiquín mensual; detector, pulsador y gabinete anual.';

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const fechaValida = (v: unknown): v is string => typeof v === 'string' && ISO.test(v);

export function estadoVencimiento(fecha: string, hoyIso: string, diasAviso = DIAS_POR_VENCER): EstadoVencimiento {
  if (fecha < hoyIso) return 'vencido';
  return fecha <= sumarDias(hoyIso, diasAviso) ? 'por_vencer' : 'vigente';
}
export function sumarDias(iso: string, dias: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  return isoLocal(new Date(a, m - 1, d + dias));
}

// ── Extintores (NTE INEN 739:2016 y AM 1257) ──
type ActivoVence = Pick<FilaActivo, 'category' | 'tech'> & { manufacture_year?: number | null };

/** Plazo de mantenimiento interno (Tabla 1 de la NTE INEN 739:2016): los de cartucho, cada año; los demás, según el agente. */
export function plazoInterno(tech: FilaActivo['tech']): ClavePlazoExtintor | null {
  const t = tech ?? {};
  if (t.presurizacion === 'cartucho') return 'internal_cartridge_months';
  switch (t.agente) {
    case 'co2': return 'internal_co2_months';
    case 'agua': return 'internal_water_months';
    case 'pqs': return 'internal_dry_chemical_months';
    case 'halogenado': return 'internal_halogenated_months';
    case 'espuma': return 'internal_foam_months';
    case 'acetato_potasio': return 'internal_wet_agent_months';
    default: return null;
  }
}

/** Fecha de fabricación: la del campo técnico o, sin ella, el 1 de enero del año de fabricación. */
export function fechaFabricacion(a: ActivoVence): string | null {
  const t = a.tech ?? {};
  if (fechaValida(t.fecha_fabricacion)) return t.fecha_fabricacion;
  const y = a.manufacture_year;
  return typeof y === 'number' && y >= 1900 && y <= 2100 ? `${y}-01-01` : null;
}

/** Vencimiento de la prueba hidrostática (AM 1257): desde la última prueba o, sin ella, desde la fabricación. El mismo cálculo que la base. */
export function venceHidrostatica(a: ActivoVence, plazos: PlazosExtintor = PLAZOS_NORMA): string | null {
  const t = a.tech ?? {};
  const base = fechaValida(t.prueba_hidrostatica) ? t.prueba_hidrostatica : fechaFabricacion(a);
  return base ? sumarMeses(base, plazos.hydrostatic_months) : null;
}

/**
 * ¿Se puede registrar esta recarga? Error para mostrar o null. La base aplica la misma regla (M50): un extintor no
 * recargable no se recarga, y la prueba hidrostática tiene que estar vigente a la fecha de la recarga.
 */
export function bloqueoRecarga(a: ActivoVence, plazos: PlazosExtintor = PLAZOS_NORMA): string | null {
  const t = a.tech ?? {};
  if (a.category !== 'extintor' || !fechaValida(t.ultima_recarga)) return null;
  if (t.recargable === 'no') return 'Un extintor no recargable no se recarga: se reemplaza (NTE INEN 739:2016).';
  const vence = venceHidrostatica(a, plazos);
  if (vence && t.ultima_recarga > vence) {
    return `La prueba hidrostática del extintor venció el ${fechaEc(vence)} (AM 1257: cada ${textoPlazo(plazos.hydrostatic_months, 'meses')}): `
      + 'regístrala antes de registrar la recarga.';
  }
  return null;
}

/** Valida los plazos que escribe la empresa: enteros desde 1 hasta el de la norma (son máximos). */
export function validarPlazos(plazos: PlazosExtintor): string | null {
  for (const p of PLAZOS_EXTINTOR) {
    const v = plazos[p.clave];
    if (!Number.isInteger(v) || v < 1) return `${p.etiqueta}: escribe un número entero mayor que 0.`;
    if (v > p.norma) return `${p.etiqueta}: el máximo es ${textoPlazo(p.norma, p.unidad)} (${p.fuente}). Se puede acortar, no alargar.`;
  }
  return null;
}

export function vencimientos(a: ActivoVence, hoy: Date = new Date(), plazos: PlazosExtintor = PLAZOS_NORMA): Vencimiento[] {
  const hoyIso = isoLocal(hoy);
  const t = a.tech ?? {};
  const out: Vencimiento[] = [];
  const poner = (clave: string, etiqueta: string, fecha: string | null, calculado: boolean, diasAviso = DIAS_POR_VENCER) => {
    if (fecha) out.push({ clave, etiqueta, fecha, estado: estadoVencimiento(fecha, hoyIso, diasAviso), calculado });
  };
  switch (a.category) {
    case 'extintor': {
      if (fechaValida(t.ultima_inspeccion_visual)) {
        poner('inspeccion_visual', 'Próxima inspección visual', sumarDias(t.ultima_inspeccion_visual, plazos.visual_inspection_days), true, DIAS_AVISO_INSPECCION_VISUAL);
      }
      if (fechaValida(t.ultimo_mantenimiento)) poner('mantenimiento', 'Próximo mantenimiento', sumarMeses(t.ultimo_mantenimiento, plazos.maintenance_months), true);
      const fab = fechaFabricacion(a);
      if (t.recargable === 'no') {
        // No recargable: no lleva mantenimiento interno ni prueba hidrostática; sale de servicio a los 12 años.
        if (fab) poner('retiro', 'Fuera de servicio (no recargable)', sumarMeses(fab, plazos.non_rechargeable_life_months), true);
      } else {
        const interno = plazoInterno(t);
        const base = fechaValida(t.ultimo_mantenimiento_interno) ? t.ultimo_mantenimiento_interno : fab;
        if (interno && base) poner('mantenimiento_interno', 'Próximo mantenimiento interno', sumarMeses(base, plazos[interno]), true);
        poner('hidrostatica', 'Próxima prueba hidrostática', venceHidrostatica(a, plazos), true);
      }
      if (t.agente === 'co2' && fechaValida(t.prueba_manguera_co2)) {
        poner('manguera_co2', 'Próxima prueba de la manguera (CO₂)', sumarMeses(t.prueba_manguera_co2, plazos.co2_hose_months), true);
      }
      break;
    }
    case 'lampara_emergencia':
      if (fechaValida(t.ultima_prueba)) poner('prueba', 'Próxima prueba de funcionamiento', sumarMeses(t.ultima_prueba, 1), true);
      break;
    case 'detector':
    case 'pulsador':
      if (fechaValida(t.ultima_prueba)) poner('prueba', 'Próxima prueba de funcionamiento', sumarMeses(t.ultima_prueba, 12), true);
      break;
    case 'botiquin':
      if (fechaValida(t.ultima_revision)) poner('revision', 'Próxima revisión del contenido', sumarMeses(t.ultima_revision, 1), true);
      if (fechaValida(t.vencimiento_insumos)) poner('insumos', 'Vencimiento de insumos', t.vencimiento_insumos, false);
      break;
    case 'gabinete':
      if (fechaValida(t.ultima_inspeccion)) poner('inspeccion', 'Próxima inspección', sumarMeses(t.ultima_inspeccion, 12), true);
      break;
    default:
  }
  return out.sort((x, y) => x.fecha.localeCompare(y.fecha));
}

/** El vencimiento más próximo (para la columna del tablero). */
export const proximoVencimiento = (a: ActivoVence, hoy?: Date, plazos?: PlazosExtintor) => vencimientos(a, hoy, plazos)[0] ?? null;

export const ETIQUETA_VENCIMIENTO: Record<EstadoVencimiento, string> = { vencido: 'Vencido', por_vencer: 'Por vencer', vigente: 'Vigente' };

// ── Resumen (tarjetas del tablero) ──
export type Tarjeta = 'total' | 'operativos' | 'con_observaciones' | 'fuera_servicio' | 'criticos';
export const ETIQUETA_TARJETA: Record<Tarjeta, string> = {
  total: 'Total', operativos: 'Operativos', con_observaciones: 'Con observaciones', fuera_servicio: 'Fuera de servicio', criticos: 'Críticos',
};
export function resumen(activos: Pick<FilaActivo, 'operational_status' | 'criticality' | 'archived_at'>[]): Record<Tarjeta, number> {
  const vivos = activos.filter(a => !a.archived_at);
  return {
    total: vivos.length,
    operativos: vivos.filter(a => a.operational_status === 'operativo').length,
    con_observaciones: vivos.filter(a => a.operational_status === 'con_observaciones').length,
    fuera_servicio: vivos.filter(a => a.operational_status === 'fuera_servicio').length,
    criticos: vivos.filter(a => a.criticality === 'critica').length,
  };
}
/** Filtro que aplica cada tarjeta al hacer clic. */
export const FILTRO_TARJETA: Record<Tarjeta, Record<string, string[]>> = {
  total: {}, operativos: { operational_status: ['operativo'] }, con_observaciones: { operational_status: ['con_observaciones'] },
  fuera_servicio: { operational_status: ['fuera_servicio'] }, criticos: { criticality: ['critica'] },
};

// ── Aplicabilidad de los ítems del Anexo 1 ──
export interface ConteoActivos { ventilacion: number; maquinas: number }
export function conteoParaChecklist(activos: Pick<FilaActivo, 'category' | 'archived_at'>[]): ConteoActivos {
  const vivos = activos.filter(a => !a.archived_at);
  return { ventilacion: vivos.filter(a => a.category === 'ventilacion').length, maquinas: vivos.filter(a => a.category === 'maquina').length };
}

/** Texto de la ubicación (sede · área · ubicación específica). */
export function textoUbicacion(a: Pick<FilaActivo, 'area' | 'location_detail'>, sede?: string | null): string {
  return [sede, a.area, a.location_detail].filter(x => !!x && String(x).trim()).join(' · ') || '—';
}

export const textoActivo = (a: Pick<FilaActivo, 'code' | 'name' | 'category'>) => `${a.code} · ${a.name} (${etiquetaCategoria(a.category)})`;
